import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { saveSelectMenu } from '@ecp/common/src/utils/utils';
import boTheme from '@ecp/common/src/style/theme/bo';
import { textEllipsis } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxCenter } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';

import { withMenu, withMenuUrl } from '@bo-recoil/common/selector';
import { useBookmarkMenu } from '@bo-hooks/common/useBookmarkMenu';
import { ReactComponent as BookmarkSelectedIcon } from '@bo-assets/icon/common/icon__bookmark--selected.svg';
import { ReactComponent as BookmarkIcon } from '@bo-assets/icon/common/icon__bookmark.svg';

const Container = styled(FlexBox)`
  width: 100%;
  height: 42px;
  background-color: ${boTheme.color.menu2};

  ${({ active }) => active && activeStyle}
`;

const activeStyle = css`
  & div {
    font-weight: ${boTheme.font.weight.bold};
  }

  background-color: ${boTheme.color.menu3};
`;

const LeafMenuName = styled.div`
  max-width: 202px;
  font-size: ${boTheme.font.size.small};
  font-weight: ${boTheme.font.weight.regular};
  color: ${boTheme.color.sub10};

  ${textEllipsis}
`;

const BookmarkIconWrapper = styled(FlexBoxCenter)`
  cursor: pointer;
  width: 24px;
  height: 22px;
`;

const LnbLeafMenu = ({ leafMenuNumber }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id } = useParams();
  const validUrl = id && pathname?.includes(id) ? pathname?.split('/' + id)[0] : pathname;

  const menu = useRecoilValue(withMenu(leafMenuNumber));
  const menuUrl = useRecoilValue(withMenuUrl(validUrl));

  const { updateBookmarkMenu } = useBookmarkMenu(leafMenuNumber);

  const leafMenuRef = useRef();
  const clickedMe = useRef(false);

  useEffect(() => {
    if (pathname?.endsWith(menu?.menuUrl) || pathname?.includes(`${menu?.menuUrl}/`)) {
      clickedMe.current || leafMenuRef.current?.scrollIntoView();
      saveSelectMenu(JSON.stringify(menu));
    }

    clickedMe.current = false;
  }, [pathname, menu?.menuUrl, menu]);

  const handleBookmarkClick = async () => {
    await updateBookmarkMenu(!menu?.isBookmark);
  };

  const handleLeafMenuClick = () => {
    clickedMe.current = true;
    navigate(menu?.menuUrl);
  };

  return (
    <Container active={pathname !== '/' && menuUrl?.menuNumber === menu?.menuNumber} ref={leafMenuRef}>
      <Spacing left={'24px'} />
      <BookmarkIconWrapper onClick={handleBookmarkClick}>
        {menu?.isBookmark ? <BookmarkSelectedIcon /> : <BookmarkIcon />}
      </BookmarkIconWrapper>
      <Spacing right={'6px'} />
      <LeafMenuName onClick={handleLeafMenuClick}>{menu?.menuName}</LeafMenuName>
    </Container>
  );
};

LnbLeafMenu.propTypes = {
  leafMenuNumber: PropTypes.string,
};

export default LnbLeafMenu;
